// src/Pages/PrivacyPolicyPage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0118] via-[#1a0f2e] to-[#0a0118] text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-slate-400 mt-1">Last Updated: January 9, 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          
          {/* Introduction */}
          <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-2xl font-bold text-blue-300 mb-4">Your Privacy Matters</h2>
            <p className="text-slate-300 leading-relaxed">
              At WHIPT (Where'd I Put That?), operated by Pronoia Wellness, we take your privacy seriously. 
              This Privacy Policy explains how we collect, use, protect, and share your information. We are 
              committed to transparency and compliance with COPPA, FERPA, and applicable data protection laws.
            </p>
          </div>

          {/* 1. Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-blue-300 mb-2">Account Information</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>Name and email address</li>
                  <li>Age/birthdate (for age verification)</li>
                  <li>Parent/guardian email (for users under 13)</li>
                  <li>School/institution (optional)</li>
                  <li>Profile preferences and settings</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-blue-300 mb-2">Content You Create</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>Class notes and journal entries</li>
                  <li>Documents and written work</li>
                  <li>Uploaded images and files</li>
                  <li>Budget and financial planning data</li>
                  <li>Calendar events and reminders</li>
                  <li>Chat messages and collaboration content</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-blue-300 mb-2">Usage Information</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  <li>Login activity and session data</li>
                  <li>Feature usage and interactions</li>
                  <li>Device type and browser information</li>
                  <li>IP address and location (general)</li>
                  <li>Error logs and performance data</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">We use your information to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Provide and improve WHIPT services</li>
              <li>Personalize your experience and save your preferences</li>
              <li>Enable collaboration features (video rooms, chat, file sharing)</li>
              <li>Process AI-assisted writing and organization features</li>
              <li>Send important updates and notifications</li>
              <li>Ensure platform security and prevent abuse</li>
              <li>Comply with legal obligations</li>
              <li>Analyze usage patterns to improve features</li>
            </ul>
          </section>

          {/* 3. Data Storage & Security */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Data Storage & Security</h2>
            <div className="space-y-4 text-slate-300">
              <p className="leading-relaxed">
                <strong className="text-blue-300">Where Your Data Lives:</strong> Your content is stored using 
                secure cloud infrastructure (Turso database) and browser local storage for certain features 
                (budget data, preferences). We use industry-standard encryption for data transmission (HTTPS/SSL).
              </p>
              <p className="leading-relaxed">
                <strong className="text-blue-300">Security Measures:</strong> We implement technical and 
                organizational measures including encryption, access controls, regular security audits, and 
                secure authentication to protect your data.
              </p>
              <p className="leading-relaxed">
                <strong className="text-blue-300">Important Note:</strong> No system is 100% secure. While we 
                take security seriously, we cannot guarantee absolute security. Please use strong passwords and 
                keep your account credentials confidential.
              </p>
            </div>
          </section>

          {/* 4. Age-Specific Privacy Protections */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Age-Specific Privacy Protections</h2>
            
            <div className="space-y-4">
              <div className="bg-amber-900/20 rounded-xl p-4 border border-amber-400/30">
                <h3 className="text-lg font-semibold text-amber-300 mb-2">🧒 Under 13 (COPPA Compliance)</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">
                  <li>Verifiable parental consent required before account creation</li>
                  <li>Parent email notification sent immediately</li>
                  <li>Restricted data collection (only necessary information)</li>
                  <li>No behavioral advertising or tracking</li>
                  <li>Limited features (no messaging, chat, or social features)</li>
                  <li>Parents can review, delete, or refuse further collection of child's data</li>
                </ul>
              </div>

              <div className="bg-blue-900/20 rounded-xl p-4 border border-blue-400/30">
                <h3 className="text-lg font-semibold text-blue-300 mb-2">📚 Ages 13-17 (Teens)</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">
                  <li>Age-appropriate content filtering enabled</li>
                  <li>Enhanced privacy settings by default</li>
                  <li>Parental notification options available</li>
                  <li>Safe, monitored communication features</li>
                </ul>
              </div>

              <div className="bg-purple-900/20 rounded-xl p-4 border border-purple-400/30">
                <h3 className="text-lg font-semibold text-purple-300 mb-2">🎓 Ages 18+ (Adults)</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">
                  <li>Full control over privacy settings</li>
                  <li>Access to all features</li>
                  <li>Standard data collection and usage policies apply</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 5. Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. How We Share Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We do NOT sell your personal information. We may share data only in these limited circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li><strong className="text-blue-300">With Your Consent:</strong> When you explicitly choose to share content with classmates or collaborate</li>
              <li><strong className="text-blue-300">Service Providers:</strong> Third-party services that help us operate WHIPT (hosting, analytics) under strict confidentiality agreements</li>
              <li><strong className="text-blue-300">Legal Requirements:</strong> When required by law, court order, or to protect rights and safety</li>
              <li><strong className="text-blue-300">Educational Institutions:</strong> With your school (if using institutional account) per FERPA guidelines</li>
              <li><strong className="text-blue-300">Business Transfers:</strong> In the event of a merger or acquisition (with notice to users)</li>
            </ul>
          </section>

          {/* 6. Your Rights & Choices */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Your Privacy Rights & Choices</h2>
            <p className="text-slate-300 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li><strong className="text-blue-300">Access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-blue-300">Correct:</strong> Update inaccurate or incomplete information</li>
              <li><strong className="text-blue-300">Delete:</strong> Request deletion of your account and data</li>
              <li><strong className="text-blue-300">Export:</strong> Download your content (journal notes, documents, budget data)</li>
              <li><strong className="text-blue-300">Opt-Out:</strong> Decline optional features or communications</li>
              <li><strong className="text-blue-300">Restrict:</strong> Limit how we process your data</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              To exercise these rights, contact us at <span className="text-blue-400">privacy@theblackbettermentquest.org</span>
            </p>
          </section>

          {/* 7. FERPA Compliance */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. FERPA Compliance (Educational Records)</h2>
            <p className="text-slate-300 leading-relaxed">
              If you use WHIPT through an educational institution, your academic content may be considered an 
              "education record" under FERPA. We comply with FERPA requirements and will not disclose such 
              records without your consent, except as permitted by FERPA (e.g., to school officials with 
              legitimate educational interest).
            </p>
          </section>

          {/* 8. Cookies & Tracking */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Cookies & Tracking Technologies</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300">
              <li>Remember your login state and preferences</li>
              <li>Analyze how you use WHIPT to improve features</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              You can control cookies through your browser settings. Note that disabling cookies may limit 
              some functionality.
            </p>
          </section>

          {/* 9. Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Third-Party Services & Links</h2>
            <p className="text-slate-300 leading-relaxed">
              WHIPT may contain links to external websites or integrate with third-party services (e.g., bank 
              websites, Google Drive). We are not responsible for the privacy practices of these third parties. 
              Please review their privacy policies separately.
            </p>
          </section>

          {/* 10. Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Data Retention</h2>
            <p className="text-slate-300 leading-relaxed">
              We retain your data as long as your account is active or as needed to provide services. When you 
              delete your account, we will delete or anonymize your personal data within 30 days, except where 
              we must retain data for legal compliance.
            </p>
          </section>

          {/* 11. International Users */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. International Data Transfers</h2>
            <p className="text-slate-300 leading-relaxed">
              WHIPT is based in the United States. If you access our services from outside the U.S., your 
              information may be transferred to and processed in the U.S. By using WHIPT, you consent to this 
              transfer.
            </p>
          </section>

          {/* 12. Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes 
              via email or in-app notification. The "Last Updated" date at the top will always reflect the most 
              recent version.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-blue-900/20 rounded-2xl p-6 border border-blue-400/30">
            <h2 className="text-2xl font-bold text-white mb-4">13. Contact Us About Privacy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Questions, concerns, or requests regarding your privacy? Contact us:
            </p>
            <ul className="space-y-2 text-slate-300">
              <li><strong className="text-blue-300">Privacy Officer:</strong> privacy@theblackbettermentquest.org</li>
              <li><strong className="text-blue-300">Support:</strong> support@theblackbettermentquest.org</li>
              <li><strong className="text-blue-300">Mail:</strong> Pronoia Wellness, [Your Address]</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              <strong className="text-blue-300">Parent Inquiries:</strong> If you are a parent/guardian with 
              questions about your child's data, please email <span className="text-blue-400">parents@theblackbettermentquest.org</span>
            </p>
          </section>

          {/* Agreement */}
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-6 border border-blue-400/30">
            <p className="text-white font-semibold text-center">
              By using WHIPT, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
